import { defineStore } from 'pinia'
import { computed, nextTick, ref, shallowRef } from 'vue'
import type { HabilitationsAdminMode, HabilitationsAdminProps, TokenData } from './HabilitationsAdmin.type'
import { useImmerFieldPatcher } from '@/features/interface/composables/ImmerFieldPatcher'
import { buildRattachementAdminService } from '@/features/services/profil/RattachementAdminService'
import type { ContactDTO, EntiteDTO } from '@/features/api/habilitation/dto'
import { deepEqual } from '@/features/shared/tools/object'
import { b64DecodeUnicode, urlSafeToStandard } from '@/features/shared/tools/b64'

type EngineState = Partial<
  HabilitationsAdminProps & {
    isRunning: boolean
    identifiantModel: ContactDTO | Entite
  }
>

export type Entite = EntiteDTO & {
  identifiant: string
}

export const useHabilitationsAdminEngineStore = defineStore('habilitations-admin', () => {
  const dataToken = ref<TokenData>()
  const engineState = shallowRef<EngineState>({})

  function $reset() {
    engineState.value = {}
    dataToken.value = undefined
  }

  const { setupCustomUpdater, setupFieldWithKey, setupFieldWithOptions } = useImmerFieldPatcher<EngineState>({
    getModel: () => engineState.value,
    setModel(model, _patch) {
      /* Gére le changement de token */
      if (model.jwtToken !== engineState.value.jwtToken) {
        if (model.jwtToken) {
          dataToken.value = extractDataFromToken(model.jwtToken)
        } else {
          dataToken.value = undefined
        }
      }

      // Gére le changement de context en mode WebComponent
      if (model.context !== engineState.value.context && model.identifiant) {
        const forceRechargementIdentifiant = model.identifiant === engineState.value.identifiant

        if (forceRechargementIdentifiant) {
          const identifiant = model.identifiant
          nextTick(() => loadIdentifiantModel(identifiant))
        }
      }

      if (!deepEqual(model.identifiantModel, engineState.value.identifiantModel)) {
        let identifiant: string | undefined
        if (model.mode === 'contact') {
          const contact = model.identifiantModel as ContactDTO | undefined
          identifiant = contact?.email
        } else if (model.mode === 'entite') {
          const entite = model.identifiantModel as Entite | undefined
          identifiant = entite?.identifiant
        }

        model = {
          ...model,
          identifiantModel: model.identifiantModel,
          identifiant,
        }
      }

      engineState.value = model
    },
  })

  async function initialise(props: HabilitationsAdminProps, isRunning?: boolean): Promise<void> {
    $reset()

    const preInit = setupCustomUpdater<HabilitationsAdminProps>((patch, props) => {
      patch.autoRun = props.autoRun
      patch.isRunning = isRunning ?? props.autoRun
      patch.context = props.context
      patch.contextReadOnly = props.contextReadOnly
      patch.identifiant = props.identifiant
      patch.itentifiantReadOnly = props.itentifiantReadOnly
      patch.identifiantModel = undefined
      patch.jwtToken = props.jwtToken
      patch.mode = props.mode
      patch.modeReadOnly = props.modeReadOnly
    })
    await preInit(props)
    await loadIdentifiantModel(engineState.value.identifiant)
  }

  async function loadIdentifiantModel(identifiant: undefined | string) {
    const estRechercheEntitéPossible =
      engineState.value.isRunning !== false &&
      engineState.value.mode &&
      engineState.value.context &&
      identifiant &&
      dataToken.value
    if (!estRechercheEntitéPossible) return

    let entite: ContactDTO | Entite | null
    if (engineState.value.mode === 'contact') {
      entite = await getContact(identifiant)
    } else {
      entite = await getEntite(identifiant)
    }
    if (entite) {
      engineState.value = {
        ...engineState.value,
        identifiantModel: entite,
        identifiant: identifiant,
      }
    }
  }

  const identifiantInterneApi = computed(() => {
    const identifiantModel = engineState.value.identifiantModel
    if (!identifiantModel) return

    return 'userId' in identifiantModel ? identifiantModel.userId : identifiantModel.identifiant
  })

  const isReady = computed(() => {
    const result =
      dataToken.value &&
      engineState.value.mode &&
      engineState.value.context &&
      identifiantInterneApi.value &&
      engineState.value.isRunning

    return !!result
  })

  const estModeEntiteSirenSiret = computed(
    () => dataToken.value?.contextTypeUser === 'Interne' && dataToken?.value?.isImpersonating === 'False',
  )

  const isReadonly = computed(() => {
    const isAdmin = dataToken.value?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'ADMIN'
    const forcedRO = engineState.value.contextReadOnly === true || engineState.value.itentifiantReadOnly === true
    const isImperso = dataToken.value?.isImpersonating === 'True'
    return forcedRO || isImperso || !isAdmin
  })

  function extractDataFromToken(token: string) {
    const rawData = urlSafeToStandard(token.split('.')[1])
    const json = b64DecodeUnicode(rawData)
    return JSON.parse(json) as TokenData
  }

  function getService() {
    const serviceReady =
      dataToken.value && engineState.value.context && engineState.value.jwtToken && engineState.value.mode

    if (!serviceReady) new Error("Le composant n'est pas prêt")

    return buildRattachementAdminService({
      baseUrl: dataToken.value!.aud,
      context: engineState.value.context!,
      token: engineState.value.jwtToken!,
      mode: engineState.value.mode!,
    })
  }

  function searchContact(search: string, signal?: AbortSignal) {
    const service = getService()
    return service.searchContact(search, { dialogMode: 'silent', signal })
  }

  function searchEntite(search: string, signal?: AbortSignal) {
    const service = getService()
    return service.searchEntite(search, { dialogMode: 'silent', signal }).then((result) =>
      result.map(
        (entite) =>
          ({
            ...entite,
            identifiant: entite.nic ? `${entite.siren}${entite.nic}` : entite.siren,
          }) as Entite,
      ),
    )
  }

  function getContact(identifiant: string) {
    const service = getService()
    return service.getContact(identifiant)
  }
  function getEntite(identifiant: string) {
    const service = getService()
    return service.getEntite(identifiant).then((result) => {
      if (!result) return result
      return {
        ...result,
        identifiant: result.nic ? `${result.siren}${result.nic}` : result.siren,
      } as Entite
    })
  }

  const allowNavigate = computed(() => {
    return engineState.value.identifiantHidden !== true && engineState.value.itentifiantReadOnly !== true
  })

  const navigateTo = async (mode: HabilitationsAdminMode, identifiant: string) => {
    if (!allowNavigate.value) return
    const props = {
      ...engineState.value,
      identifiant: identifiant,
      mode: mode,
    } as HabilitationsAdminProps

    await initialise(props)
  }

  return {
    isReady,
    isReadonly,
    dataToken,
    engineState,
    engineSetupCustomUpdater: setupCustomUpdater,
    engineSetupFieldWithKey: setupFieldWithKey,
    engineSetupFieldWithOptions: setupFieldWithOptions,
    estModeEntiteSirenSiret,
    getService,
    initialise,
    identifiantInterneApi,
    searchContact,
    searchEntite,
    allowNavigate,
    navigateTo,
  }
})
